import type { AnnotationPayload, ViewerAnnotation } from '@/lib/api';

/** Internal viewer shape used by PatchAnnotator (Konva). */
export interface ViewerAnnotationState {
  id: string;
  type: 'rectangle' | 'polygon';
  points: { x: number; y: number }[];
  label: string;
  sublabel: string;
  color: string;
  attributes?: Record<string, string>;
}

export function apiToViewer(ann: ViewerAnnotation, index: number): ViewerAnnotationState {
  const attrs = ann.attributes || {};
  const sublabel =
    attrs.cell_type ||
    attrs.Organisms ||
    attrs.organisms ||
    attrs.Neoplastic_Type ||
    attrs.Benign_or_Infection_Type ||
    '';

  if (ann.shape_type === 'bbox' && ann.coordinates) {
    const { xtl = 0, ytl = 0, xbr = 0, ybr = 0 } = ann.coordinates;
    return {
      id: ann.id || `ann-${index}`,
      type: 'rectangle',
      points: [
        { x: xtl, y: ytl },
        { x: xbr, y: ybr },
      ],
      label: ann.label,
      sublabel,
      color: ann.color || '#00ff00',
      attributes: attrs,
    };
  }

  const pts = (ann.coordinates?.points || []).map(([x, y]) => ({ x, y }));
  return {
    id: ann.id || `ann-${index}`,
    type: 'polygon',
    points: pts,
    label: ann.label,
    sublabel,
    color: ann.color || '#00ff00',
    attributes: attrs,
  };
}

export function viewerToApi(ann: ViewerAnnotationState): AnnotationPayload {
  const attributes: Record<string, string> = { ...(ann.attributes || {}) };
  if (ann.sublabel) {
    attributes.cell_type = attributes.cell_type || ann.sublabel;
  }

  if (ann.type === 'rectangle' && ann.points.length >= 2) {
    const xs = ann.points.map((p) => p.x);
    const ys = ann.points.map((p) => p.y);
    return {
      shape_type: 'bbox',
      label: ann.label,
      color: ann.color,
      attributes,
      coordinates: {
        xtl: Math.min(...xs),
        ytl: Math.min(...ys),
        xbr: Math.max(...xs),
        ybr: Math.max(...ys),
      },
    };
  }

  return {
    shape_type: 'polygon',
    label: ann.label,
    color: ann.color,
    attributes,
    coordinates: {
      points: ann.points.map((p) => [p.x, p.y]),
    },
  };
}
